import { ComponentProps } from 'react'

import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import {
  ContactFragment,
  LegalEntityForSelectFragment,
  SaleInvoiceComplianceExtraFieldFragment,
  SaleInvoiceDetailLineItemFragment,
  SaleInvoiceDetailQuery,
  SaleInvoiceDetailTaxFragment,
  SaleInvoiceUpsertSidepanelQuery,
} from 'generated/resources/graphql'

import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import type { Navigation } from 'modules/core/lib/navigation/context'
import type { NavigationPaths } from 'modules/core/lib/navigation/context'

import {
  complianceExtraFieldLabelForValue,
  sortedComplianceExtraFieldsForContext,
} from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/lib/complianceExtraFields'

import { contactFullName } from './contactsUtils'

type FinanceFinancialDocumentComplianceExtraFieldsConfig = NonNullable<
  SaleInvoiceUpsertSidepanelQuery['finance']
>['financialDocumentComplianceExtraFieldsConfigs'][number]

type FinanceComplianceSnapshotComplianceExtraField = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<SaleInvoiceDetailQuery['finance']>['complianceSnapshotsConnection']
    >['nodes']
  >[number]['financialDocumentSnapshot']
>['complianceFields'][number]

type DetailsListItem = ComponentProps<typeof DetailsItemsList>['details'][0]

export type SalesInvoiceProject = {
  id: string
  name: string
  canNavigate: boolean
}

export interface SaleInvoiceDocument {
  documentNumber?: string | null
  documentDate?: string | null
  validatedAt?: string | null
  dueDate?: string | null
  currency?: string | null
  totalAmountCents?: number | null
  discountPercentage?: number | null
  taxes?: SaleInvoiceDetailTaxFragment[]
  recipientName?: string | null
  recipientAddressLine1?: string | null
  recipientAddressLine2?: string | null
  recipientCity?: string | null
  recipientPostalCode?: string | null
  recipientState?: string | null
  recipientCountryCode?: string | null
  recipientTaxId?: string | null
  vendor?: ContactFragment | null
  complianceFields?: (
    | SaleInvoiceComplianceExtraFieldFragment
    | FinanceComplianceSnapshotComplianceExtraField
  )[]
  issuerCountryCode?: string | null
  issuerName?: string | null
  legalEntity?: LegalEntityForSelectFragment | null
  lineItems?: SaleInvoiceDetailLineItemFragment[]
}

export const formatDate = (date?: string | null) => {
  if (!date) {
    return '-'
  }
  return dateBackendDateTimeToUI(date)
}

export const buildAddress = (saleInvoice: SaleInvoiceDocument): string[] => {
  const line1 = saleInvoice.recipientAddressLine1 ?? saleInvoice.vendor?.address?.line1
  const line2 = saleInvoice.recipientAddressLine2 ?? saleInvoice.vendor?.address?.line2
  const city = saleInvoice.recipientCity ?? saleInvoice.vendor?.address?.city
  const postalCode = saleInvoice.recipientPostalCode ?? saleInvoice.vendor?.address?.postalCode
  const state = saleInvoice.recipientState ?? saleInvoice.vendor?.address?.state
  const countryCode = saleInvoice.recipientCountryCode ?? saleInvoice.vendor?.address?.countryCode

  const addressParts = [
    line1 && `${line1},`,
    line2 && `${line2},`,
    city && `${city},`,
    postalCode && `${postalCode},`,
    state && `${state},`,
    countryCode && `${countryCode}`,
  ].filter((part): part is string => Boolean(part))

  return addressParts
}

export const buildComplianceItems = (
  saleInvoice: SaleInvoiceDocument,
  complianceExtraFieldsConfigsByCountry?: FinanceFinancialDocumentComplianceExtraFieldsConfig
): { complianceItemsLeft: DetailsListItem[]; complianceItemsRight: DetailsListItem[] } => {
  const configsFiltered = complianceExtraFieldsConfigsByCountry?.fields
    ? sortedComplianceExtraFieldsForContext(
        complianceExtraFieldsConfigsByCountry?.fields,
        'document'
      )
    : []

  const invoiceFields = saleInvoice.complianceFields?.[0]?.fields
    ? saleInvoice.complianceFields[0].fields.filter((field) =>
        configsFiltered.some((config) => config.name === field.name)
      )
    : []

  const allItems: DetailsListItem[] = []

  if (configsFiltered.length > 0) {
    configsFiltered.forEach((field) => {
      const fieldFoundInInvoice = invoiceFields?.find((f) => f.name === field.name)?.value
      if (fieldFoundInInvoice) {
        allItems.push({
          title: field.label,
          content: {
            type: 'item',
            text: complianceExtraFieldLabelForValue(field, fieldFoundInInvoice),
          },
        })
      }
    })
  }

  const itemsLeft = allItems.filter((_, index) => index % 2 === 0)
  const itemsRight = allItems.filter((_, index) => index % 2 === 1)

  return {
    complianceItemsLeft: itemsLeft,
    complianceItemsRight: itemsRight,
  }
}

export const buildDetailsLeft = (
  saleInvoice: SaleInvoiceDocument,
  address: string[],
  complianceItemsLeft: DetailsListItem[],
  navigation: Navigation<NavigationPaths>
): DetailsListItem[] => {
  const items: DetailsListItem[] = []

  items.push({
    title: i18n.t('finance.sale_invoices.detail.issuer'),
    content: {
      type: 'item',
      text: saleInvoice.issuerName || saleInvoice.legalEntity?.legalName || '-',
    },
  })

  items.push({
    title: i18n.t('finance.sale_invoices.detail.header.billed_to'),
    content: {
      type: 'item',
      text: saleInvoice.recipientName || contactFullName(saleInvoice.vendor),
      action: saleInvoice.vendor?.id
        ? {
            type: 'navigate',
            href: navigation.finance.sales.clients.contactId.fullPath({
              contactId: String(saleInvoice.vendor.id),
            }),
          }
        : undefined,
    },
  })

  items.push({
    title: i18n.t('finance.sale_invoices.detail.billing_details'),
    content: {
      type: 'item',
      text: address.join(' ') || '-',
    },
  })

  items.push({
    title: i18n.t('finance.sale_invoices.detail.vat'),
    content: {
      type: 'item',
      text: saleInvoice.recipientTaxId || saleInvoice.vendor?.taxId || '-',
    },
  })

  items.push({
    title: i18n.t('finance.currency'),
    content: {
      type: 'item',
      text: saleInvoice.currency || '-',
    },
  })

  items.push(...complianceItemsLeft)

  return items
}

export const buildDetailsRight = (
  saleInvoice: SaleInvoiceDocument,
  complianceItemsRight: DetailsListItem[],
  navigation: Navigation<NavigationPaths>,
  showInvoiceNumber?: boolean,
  relatedProject?: SalesInvoiceProject
): DetailsListItem[] => {
  const items: DetailsListItem[] = []

  if (showInvoiceNumber) {
    items.push({
      title: i18n.t('finance.sale_invoices.form.invoice_number'),
      content: {
        type: 'item',
        text: saleInvoice.documentNumber || '-',
      },
    })
  }

  items.push({
    title: i18n.t('finance.sale_invoices.form.operation_date'),
    content: {
      type: 'item',
      text: formatDate(saleInvoice.documentDate),
    },
  })

  items.push({
    title: i18n.t('finance.sale_invoices.form.issue_date'),
    content: {
      type: 'item',
      text: formatDate(saleInvoice.validatedAt),
    },
  })

  items.push({
    title: i18n.t('finance.sale_invoices.form.due_date'),
    content: {
      type: 'item',
      text: formatDate(saleInvoice.dueDate),
    },
  })

  if (relatedProject) {
    items.push({
      title: i18n.t('finance.sale_invoices.form.project'),
      content: {
        type: 'item',
        text: relatedProject.name || '-',
        action: relatedProject.canNavigate
          ? {
              type: 'navigate',
              href: navigation.projects.id.fullPath({
                id: String(relatedProject.id),
              }),
            }
          : undefined,
      },
    })
  }

  items.push(...complianceItemsRight)

  return items
}
